import { AddressLink, DataContractResult } from 'Modules'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Abi } from 'viem'

import abiVestingHMC from 'abi/abi-vesting-hmc.json'
import { ContractStatusEnum } from 'constants/index'
import { publicWallet } from 'libs/viem/config'
import { convertNumberToDateTime } from 'utils/datetime'
import { logError } from 'utils/logger'

export const useVesting = ({
  contract,
  contractAddress,
}: {
  contract: any
  contractAddress: AddressLink
}) => {
  const [loading, setLoading] = useState(false)

  const [defaultData, setDefaultData] = useState<DataContractResult>({
    tokenAddress: '',
    firstRelease: null,
    startTime: '',
    cliffDuration: null,
    totalPeriods: null,
    timePerPeriod: null,
  })

  const functionName = useMemo(
    () => [
      {
        name: 'token',
        onChange: (value: any) => {
          setDefaultData((prevData: any) => ({
            ...prevData,
            tokenAddress: value,
          }))
        },
      },
      {
        name: 'firstRelease',
        onChange: (value: any) => {
          const firstRelease = Number(value) / 100
          setDefaultData((prevData: any) => ({
            ...prevData,
            firstRelease: firstRelease,
          }))
        },
      },
      {
        name: 'startTime',
        onChange: (value: any) => {
          const startTime = convertNumberToDateTime(value)
          setDefaultData((prevData: any) => ({
            ...prevData,
            startTime: startTime,
          }))
        },
      },
      {
        name: 'cliff',
        onChange: (value: any) => {
          const cliffDuration = Number(value) / 86400
          setDefaultData((prevData: any) => ({
            ...prevData,
            cliffDuration: cliffDuration,
          }))
        },
      },
      {
        name: 'timePerPeriod',
        onChange: (value: any) => {
          const timePerPeriod = Number(value) / 86400
          setDefaultData((prevData: any) => ({
            ...prevData,
            timePerPeriod: timePerPeriod,
          }))
        },
      },
      {
        name: 'totalPeriods',
        onChange: (value: any) => {
          setDefaultData((prevData: any) => ({
            ...prevData,
            totalPeriods: value,
          }))
        },
      },
    ],
    []
  )

  const getDataContract = useCallback(async () => {
    setLoading(true)
    try {
      if (contract && contractAddress) {
        const contracts = functionName.map((func) => {
          return {
            address: contractAddress,
            abi: abiVestingHMC as Abi,
            functionName: func.name,
          }
        })
        const rsDataVesting = await publicWallet.multicall({
          contracts: contracts,
        })
        if (rsDataVesting.length) {
          functionName.map((func, index: number) => {
            const data = rsDataVesting[index] || {}
            if (data.status === ContractStatusEnum.SUCCESS) {
              func.onChange(data.result?.toString())
            }
          })
        }
      }
    } catch (error) {
      logError('Error get data contract', error)
    } finally {
      setLoading(false)
    }
  }, [contract, contractAddress, functionName])

  useEffect(() => {
    getDataContract()
  }, [contract, getDataContract])

  return {
    ...defaultData,
    loading,
    getDataContract,
  }
}
